import { useEffect } from 'react';
import type { UpdateBankingDetailsRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { pushZodIssuesToTanStackForm } from '@/shared/lib/forms';
import { FormField } from '@/shared/ui/primitives';
import { FieldGroup } from '@/shared/ui/shadcn/field';
import { Button } from '@/shared/ui/shadcn/button';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';

import {
  ACCOUNT_TYPES,
  bankingDetailsFormSchema,
  useBankingDetailsForm,
  type BankingDetailsFormValues,
} from '../../model/mercury';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

export interface LinkBankAccountStepProps {
  onSubmit: (payload: UpdateBankingDetailsRequestT) => void;
  isSubmitting: boolean;
  submitError: Error | null;
  onReadyChange: (ready: boolean) => void;
}

const ACCOUNT_TYPE_OPTIONS = ACCOUNT_TYPES.map((value) => {
  const labels: Record<string, string> = {
    personalChecking: 'Personal checking',
    personalSavings: 'Personal savings',
    businessChecking: 'Business checking',
    businessSavings: 'Business savings',
  };
  return { value, label: labels[value] ?? value };
});

function ReadinessReporter({
  values,
  isSubmitting,
  onReadyChange,
}: {
  values: BankingDetailsFormValues;
  isSubmitting: boolean;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    const isValid = bankingDetailsFormSchema.safeParse(values).success;
    onReadyChange(isValid && !isSubmitting);
  }, [values, isSubmitting, onReadyChange]);
  return null;
}

export function LinkBankAccountStep({
  onSubmit,
  isSubmitting,
  submitError,
  onReadyChange,
}: LinkBankAccountStepProps) {
  const { form, validateDraft } = useBankingDetailsForm();

  const handleSubmit = () => {
    if (isSubmitting) {
      return;
    }

    const validation = validateDraft();
    if (!validation.success) {
      pushZodIssuesToTanStackForm(form, validation.error.issues);
      return;
    }

    onSubmit(validation.data.bankingDetails);
  };

  return (
    <div className="flex flex-col gap-4">
      <form.Subscribe
        selector={(state) => state.values}
        children={(values) => (
          <ReadinessReporter
            values={values}
            isSubmitting={isSubmitting}
            onReadyChange={onReadyChange}
          />
        )}
      />

      {submitError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not link bank account</AlertTitle>
          <AlertDescription>
            <div className="flex flex-col gap-2">
              <div>{submitError.message}</div>
              <div>
                <Button type="button" variant="secondary" onClick={handleSubmit}>
                  Retry
                </Button>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <form
        id={DS_STEP_FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <fieldset disabled={isSubmitting}>
          <FieldGroup>
            <FormField
              form={form}
              type="text"
              name="bankingDetails.accountHolderName"
              label="Account holder name"
              inputProps={{ autoComplete: 'name', placeholder: 'Jane Clinician' }}
            />

            <FormField
              form={form}
              type="text"
              name="bankingDetails.routingNumber"
              label="Routing number"
              inputProps={{
                inputMode: 'numeric',
                maxLength: 9,
                autoComplete: 'off',
                placeholder: '123456789',
              }}
            />

            <FormField
              form={form}
              type="text"
              name="bankingDetails.accountNumber"
              label="Account number"
              inputProps={{
                inputMode: 'numeric',
                maxLength: 17,
                autoComplete: 'off',
                placeholder: '000123456789',
              }}
            />

            <FormField
              form={form}
              type="select"
              name="bankingDetails.accountType"
              label="Account type"
              options={ACCOUNT_TYPE_OPTIONS}
            />
          </FieldGroup>
        </fieldset>
      </form>
    </div>
  );
}
